<template>
  <!-- BODY -->
  <validation-observer
    #default="{ handleSubmit, invalid }"
    ref="refFormObserver"
  >
    <!-- Form -->
    <b-form
      class="p-2"
      @submit.prevent="handleSubmit(onSubmit)"
      @reset.prevent="resetForm"
    >
      <template
        v-if="section === 'specifics'"
      >
        <!-- Quality -->
        <b-form-group
          label="Quality"
          label-for="quality"
        >
          <b-form-select
            v-model="materialData.specifics.quality"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="qualityOptions"
            input-id="quality"
          />
        </b-form-group>

        <!-- Name -->
        <validation-provider
          #default="validationContext"
          name="Name"
          rules="required"
        >
          <b-form-group
            label="Name *"
            label-for="name"
          >
            <b-form-input
              id="name"
              v-model="materialData.specifics.name"
              trim
            />
            <b-form-invalid-feedback :state="getValidationState(validationContext)">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>

        <!-- Composition -->
        <b-form-group
          label="Composition"
          label-for="composition"
        >
          <b-form-textarea
            id="composition"
            v-model="materialData.specifics.composition"
            trim
            rows="3"
            max-rows="5"
          />
        </b-form-group>

        <!-- Weight -->
        <b-form-group
          label="Weight"
          label-for="weight"
        >
          <b-form-select
            v-model="materialData.specifics.weight"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="weightOptions"
            input-id="weight"
          />
        </b-form-group>

        <!-- Weight info -->
        <b-form-group
          label="Weight Info"
          label-for="weight-info"
        >
          <b-form-textarea
            id="weight-info"
            v-model="materialData.specifics.weight_info"
            trim
            rows="3"
            max-rows="5"
          />
        </b-form-group>

        <!-- Finishing -->
        <b-form-group
          label="Finishing"
          label-for="finishing"
        >
          <v-select
            v-model="materialData.specifics.finishing"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="finishingOptions"
            :clearable="false"
            multiple
            input-id="finishing"
          />
        </b-form-group>
      </template>

      <template
        v-if="section === 'color'"
      >
        <!-- Colors -->
        <b-form-group
          label="Colors"
          label-for="colors"
        >
          <v-select
            v-model="materialData.colors"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="colorOptions"
            :reduce="val => val.value"
            multiple
            :clearable="false"
            input-id="colors"
          >
            <template v-slot:option="option">
              <span
                :style="{backgroundColor: option.color,
                         marginRight: '5px', width: '20px', height:'20px',
                         borderRadius: '4px', display:'inline-block'}"
              /><span>{{ option.label }}</span>
            </template>
          </v-select>
        </b-form-group>
      </template>

      <template
        v-if="section === 'images'"
      >
        <!-- Image -->
        <style-media-grid-form
          :style-media="styleMedia"
          :submit-handler="submitHandler"
          :fetch-style-material-media="fetchStyleMaterialMedia"
        />
      </template>

      <template
        v-if="section === 'sourcing'"
      >
        <!-- Reference -->
        <b-form-group
          label="Ref"
          label-for="ref"
        >
          <b-form-input
            id="ref"
            v-model="materialData.ref"
            trim
          />
        </b-form-group>

        <!-- Supplier -->
        <b-form-group
          label="Supplier"
          label-for="supplier"
        >
          <b-form-select
            v-model="materialData.supplier"
            :options="supplierOptions"
            input-id="supplier"
          />
        </b-form-group>

        <!-- Price -->
        <b-form-group
          label="Price"
          label-for="price"
        >
          <b-form-input
            id="price"
            v-model="materialData.price"
            trim
            placeholder="0.00"
          />
        </b-form-group>
      </template>

      <template
        v-if="section === 'consumption'"
      >
        <!-- Useful width -->
        <b-form-group
          label="Useful width"
          label-for="useful-width"
        >
          <b-input-group>
            <b-form-input
              id="useful-width"
              v-model="materialData.consumption.useful_width"
              trim
            />
            <b-input-group-append>
              <b-form-select
                v-model="materialData.consumption.useful_width_units"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="usefulWidthUnitsOptions"
                input-id="useful-width-units"
              />
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <!-- Quantity -->
        <validation-provider
          #default="validationContext"
          name="Quantity"
        >
          <b-form-group
            label="Quantity"
            label-for="quantity"
          >
            <b-input-group>
              <b-form-input
                id="quantity"
                v-model="materialData.consumption.quantity"
                :state="getValidationState(validationContext)"
                trim
              />
              <b-input-group-append>
                <b-form-select
                  v-model="materialData.consumption.quantity_units"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="quantityUnitsOptions"
                />
              </b-input-group-append>
            </b-input-group>
            <b-form-invalid-feedback>
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </b-form-group>
        </validation-provider>
      </template>

      <template
        v-if="section === 'placement'"
      >
        <!-- Placement -->
        <b-form-group
          label="Placement"
          label-for="placement"
        >
          <b-form-textarea
            id="placement"
            v-model="materialData.placement"
            trim
            rows="3"
            max-rows="5"
          />
        </b-form-group>
      </template>

      <!-- Form Actions -->
      <div class="d-flex mt-2">
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          class="mr-2"
          type="submit"
          :disabled="invalid"
        >
          Next
        </b-button>
      </div>
    </b-form>
  </validation-observer>
</template>

<script>
import _ from 'lodash'
import {
  BButton, BForm, BFormGroup, BFormInput,
  BFormInvalidFeedback, BFormSelect, BFormTextarea,
  BInputGroup, BInputGroupAppend,
} from 'bootstrap-vue'

import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref, watch } from '@vue/composition-api'
import { required } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import router from '@/router'

import StyleMediaGridForm from './StyleMediaGridForm.vue'

export default {
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormSelect,
    BFormTextarea,
    BInputGroup,
    BInputGroupAppend,
    vSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,

    StyleMediaGridForm,
  },
  directives: {
    Ripple,
  },
  props: {
    categoryType: {
      type: String,
      required: true,
    },
    colorOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    supplierOptions: {
      type: Array,
      required: false,
      default: () => [],
    },
    section: {
      type: String,
      required: true,
    },
    submitHandler: {
      type: Function,
      required: false,
      default: null,
    },
    fetchStyleMaterialMedia: {
      type: Function,
      required: false,
      default: null,
    },
    styleMedia: {
      type: Array,
      required: false,
      default: () => [],
    },
    initData: {
      type: Object,
      required: false,
      default: () => ({
        specifics: {}, consumption: {},
      }),
    },
  },
  data() {
    return {
      required,
    }
  },
  setup(props) {
    const qualityOptions = [
      'Corduroy',
      'Fleece',
      'Interlock',
      'Jersey',
      'Linen',
      'Piquet',
      'Popline',
      'Rib',
      'Satine',
      'Seersucker',
      'Terry',
      'Twill',
      'Velour',
      'Woven',
      'Other',
    ]
    const weightOptions = [
      '<60 g/m²',
      '60-80 g/m²',
      '80-90 g/m²',
      '90-100 g/m²',
      '100-110 g/m²',
      '110-120 g/m²',
      '120-160 g/m²',
      '160-180 g/m²',
      '180-200 g/m²',
      '200-240 g/m²',
      '240-270 g/m²',
      '270-300 g/m²',
      '300-320 g/m²',
      '320-350 g/m²',
      '350-400 g/m²',
      '400-500 g/m²',
      '>500 g/m²',
    ]
    const finishingOptions = [
      'Brushed',
      'Loopback',
      'Carbon',
      'Handsilk',
      'Silicone',
      'Anti-bacterial',
      'Other',
    ]
    const usefulWidthUnitsOptions = [
      'mm',
      'cm',
    ]
    const quantityUnitsOptions = [
      'per garment',
    ]

    const materialDataInit = {
      specifics: {},
      consumption: {},
    }
    const materialData = ref({ ...materialDataInit })

    const resetMaterialData = () => {
      materialData.value = { ...materialDataInit }
    }

    watch(() => props.initData, newValue => {
      if (newValue !== undefined) {
        materialData.value = _.cloneDeep(newValue)
      }
    }, { deep: true, immediate: true })

    const onSubmit = () => {
      const { section } = props
      let formData = {}

      switch (section) {
        case 'specifics':
        case 'consumption':
        case 'placement':
          formData = { [section]: materialData.value[section] }
          break
        case 'color':
          formData = { colors: materialData.value.colors }
          break
        case 'sourcing':
          formData = {
            ref: materialData.value.ref,
            supplier: materialData.value.supplier,
            price: materialData.value.price,
          }
          break
        default:
          formData = {}
      }
      const categoryTypes = {
        'main-fabric': 'Main Fabric',
        'second-fabric': 'Second Fabric',
        'trims-fabric': 'Trims Fabric',
        lining: 'Lining',
        rib: 'Rib',
      }
      props.submitHandler({
        id: materialData.value.id || router.currentRoute.params.id,
        category_type: categoryTypes[props.categoryType] || 'Fabric',
        ...formData,
      })
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetMaterialData)

    return {
      qualityOptions,
      weightOptions,
      finishingOptions,
      usefulWidthUnitsOptions,
      quantityUnitsOptions,
      materialData,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    }
  },
}
</script>
